import { type IWidgetConfigComponentProps } from '@framework/engine/schema/directives/IWidgetDirectiveType.ts';
import { WidgetConfigurationSlotRender } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlots.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { WidgetConfigurationSubCard } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationSubCard.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { IconUserSquare } from '@tabler/icons-react';
import { Typography } from 'antd';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
  getSubjectDataTypesForType,
  SubjectDataType,
} from './ISubjectDataDirective.ts';
import { type ISubjectDataType } from './SubjectDataTypes.ts';
import { SubjectDataTypeTag } from './SubjectDataTypeTag.tsx';

function mapDataTypeToList(list: ISubjectDataType[]) {
  return list.map(type => ({
    value: type.code,
    searchable: type.label + ' ' + type.description,
    label: (
      <>
        {type.label}{' '}
        {type.description && (
          <Typography.Text type="secondary">{type.description}</Typography.Text>
        )}
      </>
    ),
  }));
}

export type ISubjectDataDirectiveConfigComponent = IWidgetConfigComponentProps;

/**
 * Sub-card "Significato di sistema": identifica il valore del campo come
 * attributo del Soggetto della Richiesta (Titolare, Subentrante, Azienda...).
 */
export function SubjectDataDirectiveConfigComponent(
  props: ISubjectDataDirectiveConfigComponent,
) {
  const { name } = props;

  // NOTA: non usare `defaultValue` di useWatch: prima del mount del form
  // RHF ritorna il defaultValue IGNORANDO i valori reali già presenti,
  // facendo montare la select degli attributi con la lista sbagliata
  // (che resetta il valore configurato via useFilterSelectValuesOnOptionsChange).
  // Senza fallback: il chip di riepilogo appare solo se configurato.
  const summaryType = useWatch({ name: `${name}.type` });
  const selectedType = summaryType ?? SubjectDataType.Applicant;

  const availableAttributes = useMemo(
    () =>
      mapDataTypeToList(getSubjectDataTypesForType(selectedType)?.List ?? []),
    [selectedType],
  );

  // Il reset dell'attributo non coerente col tipo selezionato è gestito
  // dalla transform di `SubjectDataDirectiveSchema` (vedi
  // `resetIncoherentCode`), applicata live da `syncComputedValues`.

  return (
    <WidgetConfigurationSlotRender
      slot={WidgetConfigurationSlots.Semantic}
      order={10}
    >
      <WidgetConfigurationSubCard
        icon={<TbIcon icon={IconUserSquare} />}
        title="Significato di sistema"
        anchorTitle="significato-di-sistema"
        description="Permette l'identificazione nel sistema del Titolare, del Subentrante o Azienda."
        summary={
          summaryType ? <SubjectDataTypeTag type={summaryType} /> : undefined
        }
      >
        <RowElement gutter={6}>
          <FormItem
            name={`${name}.type`}
            label="Tipo di soggetto"
            layout={{ removeHelpMargin: true }}
          >
            <SelectInput
              style={{ width: '100%' }}
              name={`${name}.type`}
              options={Object.values(SubjectDataType).map(value => ({
                value,
                label: <SubjectDataTypeTag type={value} />,
              }))}
              allowClear
              searchable
            />
          </FormItem>
          <FormItem
            colSpan={2}
            layout={{ removeHelpMargin: true }}
            name={`${name}.code`}
            label="Attributo del soggetto della richiesta"
          >
            <SelectInput
              name={`${name}.code`}
              optionFilterProp="searchable"
              options={availableAttributes}
              allowClear
              placeholder="Seleziona attributo..."
              searchable
              disabled={!selectedType}
            />
          </FormItem>
        </RowElement>
        <VerticalSpace size={12} />

        <Typography.Text type="secondary">
          Il valore del campo sarà trattato come attributo del Soggetto della
          Richiesta, ad esempio: Nome del Titolare.
        </Typography.Text>
      </WidgetConfigurationSubCard>
    </WidgetConfigurationSlotRender>
  );
}
